import { type MouseEvent, useCallback, useState } from "react";
import { styled, type BackdropProps, Menu as MUIMenu, type MenuProps as MUIMenuProps } from "~/common/mui";
import { C_OUTLINE_VARIANT, C_PRIMARY } from "../icon/Colors";
import {
  HEADER_HEIGHT_DESKTOP,
  HEADER_HEIGHT_PHONE,
  HEADER_HEIGHT_PHONE_BIG,
  HEADER_HEIGHT_TABLET,
  MQ_HEADER_BREAKPOINT_LOGO,
} from "../../../utils/constants";
import { MENU_POS_BELOW } from "./MenuPositions";
import useMediaQuery from "../../hooks/useMediaQuery";
import { MEDIA_QUERY_MAX_PHONE, MEDIA_QUERY_MIN_DESKTOP_S, MEDIA_QUERY_MIN_TABLET } from "~/common/utils/screenSizes";
import { useSchaefflerTopBannerList } from "~/features/cms/components/schaefflerTopBanner/SchaefflerTopBannerComponent";
import { useActiveCampaignsNotInSmartEdit } from "~/features/navigation/components/TopBannersSection/utils";
import { showRegionBanner } from "~/features/regionSelection/components/RegionSelectionBanner";
import { useSession } from "~/app/config/useSession";

export type MenuProps = MUIMenuProps;

export const Menu = styled(MUIMenu)({
  "& .MuiPaper-root": {
    boxShadow: "none",
    border: `1px solid ${C_OUTLINE_VARIANT}`,
    borderRadius: "0px",
    maxHeight: `calc(100% - ${HEADER_HEIGHT_PHONE})`,
    [`@media ${MQ_HEADER_BREAKPOINT_LOGO}`]: {
      maxHeight: `calc(100% - ${HEADER_HEIGHT_PHONE_BIG})`,
    },
    [`@media ${MEDIA_QUERY_MIN_TABLET}`]: {
      maxHeight: `calc(100% - ${HEADER_HEIGHT_TABLET})`,
    },
    [`@media ${MEDIA_QUERY_MIN_DESKTOP_S}`]: {
      maxHeight: `calc(100% - ${HEADER_HEIGHT_DESKTOP})`,
    },
  },
});

export const navigationMenuPaperStyles = {
  [`@media ${MEDIA_QUERY_MIN_TABLET}`]: {
    translate: "0 -3px",
    borderTop: `3px solid ${C_PRIMARY}`,
  },
};

const spaceInNavigationMenuPaperStyles = {
  [`@media ${MEDIA_QUERY_MIN_TABLET}`]: {
    borderRadius: "0px 0px 4px 4px",
    translate: "0 -3px",
  },
  [`@media ${MEDIA_QUERY_MAX_PHONE}`]: {
    left: "16px !important",
  },
};

/**
 * @deprecated Use styled(Menu) with navigationMenuPaperStyles instead
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const withNavigationMenuStyle = (Component: any) => {
  return styled(Component)({
    "& .MuiPaper-root": navigationMenuPaperStyles,
  });
};

/**
 * @deprecated Use styled(Menu) with spaceInNavigationMenuPaperStyles instead
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const withSpaceInNavigationMenuStyle = (Component: any) => {
  return styled(Component)({
    "& .MuiPaper-root": spaceInNavigationMenuPaperStyles,
  });
};

/**
This function is get the visible height of the banner (i.e. the part that's currently in the viewport), not the full height of the element.
*/

function getVisibleHeightBySelector(selector: string): number {
  const el = document.querySelector(selector);
  if (!(el instanceof HTMLElement)) {
    return 0;
  }

  const rect = el.getBoundingClientRect();
  const windowHeight = window.innerHeight;

  // Top is below the viewport bottom OR bottom is above the viewport top
  if (rect.bottom <= 0 || rect.top >= windowHeight) {
    return 0;
  }

  const visibleTop = Math.max(rect.top, 0);
  const visibleBottom = Math.min(rect.bottom, windowHeight);

  return visibleBottom - visibleTop;
}

export const MenuBottomCornersRounded = styled(Menu)({
  "& .MuiPaper-root": navigationMenuPaperStyles,
});

const fullwidthPopoverStyles = {
  width: "100%",
  left: 0,
  right: 0,
  maxWidth: "unset",
} as const;

/**
 * @deprecated Use fullwidthPopoverStyles with sx prop instead
 */
export const useFullwidthPopoverStyles = () => ({
  paper: "scha-fullwidth-popover-paper",
});

/**
 * Menu with some default configuration.
 *
 * It becomes a full width menu on small (Up to XS size) screens
 */
export const SimpleMenu = (props: MenuProps) => {
  const isMinTablet = useMediaQuery(MEDIA_QUERY_MIN_TABLET);

  let DEFAULT_SAFETY_MARGIN = 5;

  const smartEditBanners = useSchaefflerTopBannerList();
  const campaignsNotInSmartEdit = useActiveCampaignsNotInSmartEdit();
  const showRedirectBanner = useSession()?.regionSelection.showRedirectBanner ?? false;
  if (smartEditBanners.length || campaignsNotInSmartEdit.length) {
    DEFAULT_SAFETY_MARGIN += getVisibleHeightBySelector(".scha__root-sales-banner");
  }

  if (showRegionBanner(showRedirectBanner)) {
    DEFAULT_SAFETY_MARGIN += getVisibleHeightBySelector(".scha__root-region-banner");
  }

  const dynamicBackdropProps = {
    ...FULLWIDTH_MENU_BACKDROP_PROPS,
    style: {
      "--safety-margin": `${DEFAULT_SAFETY_MARGIN}px`,
    } as React.CSSProperties,
  };

  const viewportSpecificProps = isMinTablet
    ? {}
    : {
        sx: { "& .MuiPaper-root": fullwidthPopoverStyles },
        marginThreshold: 0,
        BackdropProps: dynamicBackdropProps,
        ...MENU_POS_BELOW,
      };

  return (
    <Menu
      MenuListProps={{
        disablePadding: true,
        ...(props.MenuListProps ?? {}),
      }}
      {...viewportSpecificProps}
      {...props}
    />
  );
};

/**
 * Unrelated to SimpleMenu above
 */
export const useSimpleMenuState = () => {
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const handleClick = (e: MouseEvent) => setAnchorEl(e.currentTarget ?? e.target);
  const handleClose = useCallback(() => setAnchorEl(null), []);

  const isOpen = anchorEl !== null;

  return [isOpen, handleClick, handleClose, anchorEl, setAnchorEl] as const;
};

// Menu -> Popover -> Modal uses an internal SimpleBackdrop which isn't the same as Backdrop and does not actually accept BackdropProps.
// It passes all unknown properites to the div though, so we can use className to achieve the end goal
export const FULLWIDTH_MENU_BACKDROP_PROPS = {
  invisible: false,
  className: "scha__fake-partial-backdrop",
} as Partial<BackdropProps>;
